import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown, ChevronUp, FileText, Loader2, Plus, RotateCcw, Upload, ShieldCheck, ShieldAlert, Clock, Eraser } from 'lucide-react';
import { useNotifications } from '../../hooks/useNotifications';
import { useConfirm, usePrompt } from '../../contexts/ConfirmContext';
import { ActionButton, Button, IconButton } from '../UI';
import { useCopyToClipboard } from '../../hooks/useCopyToClipboard';
import {
  AdminChecklist,
  ChecklistFile,
  ChecklistItem,
  ChecklistStatus,
  ChecklistTemplate,
  Lang,
  Side,
  VISA_TYPES,
  VisaType,
  absoluteUrl,
  apiFileUrl,
  errorMessage,
  itemText,
  prepareUpload,
  sidesOf,
} from '../DocumentChecklist/documentChecklist';

const STATUS_LABELS: Record<ChecklistStatus, { label: string; className: string }> = {
  awaiting_client: { label: 'Aguardando cliente', className: 'bg-amber-100 text-amber-800' },
  submitted: { label: 'Enviado — revisar', className: 'bg-orange-100 text-orange-800' },
  completed: { label: 'Concluído', className: 'bg-green-100 text-green-800' },
  revoked: { label: 'Revogado', className: 'bg-gray-200 text-gray-700' },
  expired: { label: 'Expirado', className: 'bg-gray-200 text-gray-700' },
};

const SIDE_LABELS: Record<Side, string> = { single: 'Documento', front: 'Frente', back: 'Verso' };

const SHARE_LINK_MINUTES = 60;

// Mensagem pronta para WhatsApp, no idioma do link
const CLIENT_MESSAGE: Record<Lang, (name: string, url: string) => string> = {
  pt: (name, url) =>
    `Olá${name ? `, ${name}` : ''}! Preparamos a lista de documentos do seu processo. Por este link você marca o que já tem e envia as fotos (frente e verso quando pedido), direto do celular:\n${url}`,
  en: (name, url) =>
    `Hello${name ? `, ${name}` : ''}! We have prepared the document list for your case. Use this link to tick what you already have and upload the photos (front and back when requested), right from your phone:\n${url}`,
  es: (name, url) =>
    `¡Hola${name ? `, ${name}` : ''}! Preparamos la lista de documentos de su proceso. En este enlace puede marcar lo que ya tiene y enviar las fotos (anverso y reverso cuando se pida), desde el móvil:\n${url}`,
};

function formatDate(value?: string | null) {
  return value ? new Date(value).toLocaleDateString('pt-BR') : '—';
}

function formatDateTime(value?: string | null) {
  return value ? new Date(value).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—';
}

interface Props {
  clienteId: string;
  clienteNome: string;
  clienteEmail?: string;
  onChanged?: () => void;
}

const ClienteDocumentosSection: React.FC<Props> = ({ clienteId, clienteNome, clienteEmail, onChanged }) => {
  const { t } = useTranslation();
  const notifications = useNotifications();
  const confirm = useConfirm();
  const prompt = usePrompt();
  const [checklists, setChecklists] = useState<AdminChecklist[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);

  const fetchChecklists = useCallback(async () => {
    try {
      const res = await axios.get<AdminChecklist[]>('/documentos-cliente', { params: { clienteId } });
      setChecklists(res.data);
      setExpanded((prev) => prev ?? res.data.find((c) => c.status === 'submitted')?._id ?? null);
    } catch {
      notifications.error.network();
    } finally {
      setLoading(false);
    }
  }, [clienteId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchChecklists();
  }, [fetchChecklists]);

  const replace = (doc: AdminChecklist) => setChecklists((prev) => prev.map((c) => (c._id === doc._id ? doc : c)));

  const action = async (doc: AdminChecklist, path: string, body?: object) => {
    try {
      const res = await axios.post<AdminChecklist>(`/documentos-cliente/${doc._id}/${path}`, body || {});
      replace(res.data);
      onChanged?.();
    } catch (err) {
      notifications.showError('Erro', errorMessage(err, 'Não foi possível concluir a ação.'));
    }
  };

  const handleDelete = async (doc: AdminChecklist) => {
    const ok = await confirm({
      title: 'Excluir checklist',
      message: 'O checklist e todos os arquivos enviados serão apagados permanentemente.',
      confirmText: 'Excluir',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      await axios.delete(`/documentos-cliente/${doc._id}`);
      setChecklists((prev) => prev.filter((c) => c._id !== doc._id));
    } catch (err) {
      notifications.showError('Erro', errorMessage(err, 'Não foi possível excluir.'));
    }
  };

  const changeRetention = async (doc: AdminChecklist) => {
    const typed = await prompt({
      title: 'Prazo de eliminação dos arquivos',
      label: 'Dias após a conclusão (1 a 365). Deixe vazio para usar o padrão.',
      placeholder: String(doc.retentionDays),
      confirmText: 'Salvar',
    });
    if (typed === null) return;
    const days = typed.trim() ? Number(typed.trim()) : null;
    if (days !== null && (!Number.isInteger(days) || days < 1 || days > 365)) {
      notifications.showError('Prazo inválido', 'Informe um número de 1 a 365.');
      return;
    }
    try {
      const res = await axios.patch<AdminChecklist>(`/documentos-cliente/${doc._id}`, { retentionDays: days });
      replace(res.data);
    } catch (err) {
      notifications.showError('Erro', errorMessage(err, 'Não foi possível salvar o prazo.'));
    }
  };

  const purgeFiles = async (doc: AdminChecklist) => {
    const ok = await confirm({
      title: 'Excluir arquivos agora',
      message:
        'Todos os arquivos enviados neste pedido serão eliminados definitivamente. O registro dos documentos recebidos, da revisão e do aceite do cliente continua guardado.',
      confirmText: 'Excluir arquivos',
      variant: 'danger',
    });
    if (!ok) return;
    await action(doc, 'purge-files');
  };

  const firstName = clienteNome.trim().split(/\s+/)[0] || '';

  return (
    <div className="bg-white rounded-lg shadow p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Documentos</h3>
        {!showForm && (
          <Button size="sm" className="w-full sm:w-auto sm:self-start" type="button" onClick={() => setShowForm(true)}>
            <Plus className="w-4 h-4" />
            Solicitar documentos
          </Button>
        )}
      </div>

      {showForm && (
        <NewChecklistForm
          clienteId={clienteId}
          clienteEmail={clienteEmail}
          onCancel={() => setShowForm(false)}
          onCreated={(doc) => {
            setShowForm(false);
            setChecklists((prev) => [doc, ...prev]);
            setExpanded(doc._id);
            onChanged?.();
          }}
        />
      )}

      {loading ? (
        <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
      ) : checklists.length === 0 ? (
        !showForm && <p className="text-sm text-gray-500">Nenhum documento solicitado ainda.</p>
      ) : (
        <ul className="space-y-3">
          {checklists.map((doc) => {
            const open = expanded === doc._id;
            const status = STATUS_LABELS[doc.status];
            const pct = doc.progress.total ? Math.round((doc.progress.uploaded / doc.progress.total) * 100) : 0;
            const linkActive = doc.status === 'awaiting_client' || doc.status === 'submitted';
            return (
              <li key={doc._id} className="border border-gray-200 rounded-lg">
                <button
                  type="button"
                  onClick={() => setExpanded(open ? null : doc._id)}
                  className="w-full text-left p-3 sm:p-4 flex items-start justify-between gap-3"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-gray-900">{t(`visaForm.goals.${doc.visaType}`)}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${status.className}`}>{status.label}</span>
                      <span className="text-xs text-gray-400 uppercase">{doc.lang}</span>
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      <div className="h-1.5 flex-1 max-w-xs rounded-full bg-gray-200 overflow-hidden">
                        <div className="h-full bg-primary-600" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-xs text-gray-600 tabular-nums">
                        {doc.progress.uploaded}/{doc.progress.total} enviados
                        {doc.progress.approved > 0 && ` · ${doc.progress.approved} aprovados`}
                        {doc.progress.rejected > 0 && ` · ${doc.progress.rejected} recusados`}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Solicitado em {formatDate(doc.createdAt)} · válido até {formatDate(doc.expiresAt)}
                      {doc.submittedAt && ` · enviado em ${formatDate(doc.submittedAt)}`}
                    </p>
                  </div>
                  {open ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                </button>

                {open && (
                  <div className="border-t border-gray-200 p-3 sm:p-4 space-y-4">
                    <div className="grid gap-2 sm:grid-cols-2 text-xs">
                      <div
                        className={`flex items-start gap-2 rounded-md px-3 py-2 ${
                          doc.consent ? 'bg-green-50 text-green-900' : 'bg-amber-50 text-amber-900'
                        }`}
                      >
                        {doc.consent ? (
                          <ShieldCheck className="w-4 h-4 flex-shrink-0 mt-px" />
                        ) : (
                          <ShieldAlert className="w-4 h-4 flex-shrink-0 mt-px" />
                        )}
                        <span>
                          {doc.consent ? (
                            <>
                              Termo aceito em {formatDateTime(doc.consent.acceptedAt)} · IP {doc.consent.ip || '—'} · versão{' '}
                              <Link to="/admin/documentos?tab=termo" className="underline">
                                {doc.consent.version}
                              </Link>{' '}
                              ({doc.consent.lang})
                            </>
                          ) : (
                            'O cliente ainda não aceitou o termo de consentimento (os envios ficam bloqueados até lá).'
                          )}
                        </span>
                      </div>
                      <div className="flex items-start gap-2 rounded-md px-3 py-2 bg-gray-50 text-gray-800">
                        <Clock className="w-4 h-4 flex-shrink-0 mt-px" />
                        <span>
                          {doc.filesPurgedAt
                            ? `Arquivos eliminados em ${formatDate(doc.filesPurgedAt)}.`
                            : doc.filesPurgeAt
                              ? `Arquivos serão eliminados em ${formatDate(doc.filesPurgeAt)} (${doc.retentionDays} dias após a conclusão).`
                              : `Arquivos eliminados ${doc.retentionDays} dias após a conclusão${doc.retentionIsDefault ? ' (padrão)' : ''}.`}{' '}
                          {!doc.filesPurgedAt && (
                            <button type="button" onClick={() => changeRetention(doc)} className="inline-block py-1 underline text-primary-700">
                              alterar
                            </button>
                          )}
                        </span>
                      </div>
                    </div>

                    <div className="space-y-3 md:space-y-0 md:flex md:flex-wrap md:gap-2">
                      <div>
                        <p className="md:hidden text-xs font-medium text-gray-500 mb-1.5">Link do cliente</p>
                        <div className="grid grid-cols-3 gap-2 md:flex md:flex-wrap">
                          {linkActive && (
                            <>
                              <ActionButton action="copyLink" labelVisibility="always" value={doc.formUrl} />
                              <ActionButton
                                action="copy"
                                label="Copiar mensagem"
                                labelVisibility="always"
                                value={CLIENT_MESSAGE[doc.lang](firstName, doc.formUrl)}
                              />
                              <ActionButton action="open" label="Ver como cliente" labelVisibility="always" href={doc.formUrl} />
                            </>
                          )}
                          <ActionButton
                            icon={RotateCcw}
                            label={linkActive ? 'Renovar 30 dias' : 'Reabrir link (30 dias)'}
                            labelVisibility="always"
                            onClick={() => action(doc, 'renew', { expiresInDays: 30 })}
                          />
                          {linkActive && (
                            <ActionButton action="revoke" label="Revogar link" labelVisibility="always" onClick={() => action(doc, 'revoke')} />
                          )}
                        </div>
                      </div>
                      <div>
                        <p className="md:hidden text-xs font-medium text-gray-500 mb-1.5">Pedido</p>
                        <div className="grid grid-cols-3 gap-2 md:flex md:flex-wrap">
                          {doc.status !== 'completed' && (
                            <ActionButton action="approve" label="Marcar concluído" labelVisibility="always" onClick={() => action(doc, 'complete')} />
                          )}
                          {!doc.filesPurgedAt && doc.items.some((i) => i.files.length > 0) && (
                            <ActionButton
                              icon={Eraser}
                              variant="danger"
                              label="Excluir arquivos agora"
                              labelVisibility="always"
                              onClick={() => purgeFiles(doc)}
                            />
                          )}
                          <ActionButton action="delete" labelVisibility="always" onClick={() => handleDelete(doc)} />
                        </div>
                      </div>
                    </div>

                    <ul className="divide-y divide-gray-100">
                      {doc.items.map((item) => (
                        <AdminItemRow key={item.key} doc={doc} item={item} onChange={replace} />
                      ))}
                    </ul>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

const AdminItemRow: React.FC<{
  doc: AdminChecklist;
  item: ChecklistItem;
  onChange: (doc: AdminChecklist) => void;
}> = ({ doc, item, onChange }) => {
  const notifications = useNotifications();
  const prompt = usePrompt();
  const confirm = useConfirm();
  const [busySide, setBusySide] = useState<Side | null>(null);
  const { copy } = useCopyToClipboard();
  const inputRef = useRef<HTMLInputElement>(null);
  const pendingSide = useRef<Side>('single');
  const text = itemText(item, 'pt');
  const base = `/documentos-cliente/${doc._id}`;

  const review = async (status: 'approved' | 'rejected' | 'pending') => {
    let note = '';
    if (status === 'rejected') {
      const typed = await prompt({
        title: 'Recusar documento',
        label: 'Motivo (o cliente verá este texto)',
        placeholder: 'Ex.: foto desfocada, falta o verso, documento vencido',
        confirmText: 'Recusar',
      });
      if (typed === null) return;
      note = typed;
    }
    try {
      const res = await axios.patch<AdminChecklist>(`${base}/items/${item.key}/review`, { status, note });
      onChange(res.data);
    } catch (err) {
      notifications.showError('Erro', errorMessage(err, 'Não foi possível salvar a revisão.'));
    }
  };

  const pickFor = (side: Side) => {
    pendingSide.current = side;
    inputRef.current?.click();
  };

  const upload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files?.[0];
    e.target.value = '';
    if (!picked) return;
    const side = pendingSide.current;
    setBusySide(side);
    try {
      const fd = new FormData();
      fd.append('file', await prepareUpload(picked));
      const res = await axios.post<AdminChecklist>(`${base}/items/${item.key}/files/${side}`, fd);
      onChange(res.data);
    } catch (err) {
      notifications.showError('Erro', errorMessage(err, 'Não foi possível enviar o arquivo.'));
    } finally {
      setBusySide(null);
    }
  };

  const removeFile = async (file: ChecklistFile) => {
    if (!(await confirm({ title: 'Remover arquivo', message: 'O arquivo será apagado.', confirmText: 'Remover', variant: 'danger' }))) return;
    try {
      const res = await axios.delete<AdminChecklist>(`${base}/items/${item.key}/files/${file._id}`);
      onChange(res.data);
    } catch (err) {
      notifications.showError('Erro', errorMessage(err, 'Não foi possível remover o arquivo.'));
    }
  };

  const copyTempLink = async (file: ChecklistFile) => {
    try {
      const res = await axios.post<{ path: string }>(`${base}/files/${file._id}/temp-link`, {
        expiresInMinutes: SHARE_LINK_MINUTES,
      });
      await copy(absoluteUrl(apiFileUrl(res.data.path)), file._id, {
        message: `Link temporário válido por ${SHARE_LINK_MINUTES} minutos.`,
      });
    } catch (err) {
      notifications.showError('Erro', errorMessage(err, 'Não foi possível gerar o link.'));
    }
  };

  const reviewStatus = item.review?.status || 'pending';
  const hasFiles = item.files.length > 0;

  return (
    <li className="py-3">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900">
            {text.label}
            {item.required && <span className="text-red-500 ml-0.5">*</span>}
          </p>
          <p className="text-xs text-gray-500">
            {item.sides === 'front_back' ? 'Frente e verso' : 'Arquivo único'}
            {item.clientChecked && ' · cliente marcou que tem'}
          </p>
          {reviewStatus === 'rejected' && item.review.note && (
            <p className="text-xs text-red-700 mt-1">Recusado: {item.review.note}</p>
          )}
        </div>
        <div className="flex items-center gap-2 sm:gap-1.5 flex-shrink-0 w-full sm:w-auto">
          {reviewStatus === 'approved' ? (
            <>
              <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-green-100 text-green-800">
                <Check className="w-3.5 h-3.5" /> Aprovado
              </span>
              <button type="button" onClick={() => review('pending')} className="link-action text-xs text-gray-500 hover:text-gray-800 underline">
                desfazer
              </button>
            </>
          ) : (
            <div className="grid grid-cols-2 gap-2 w-full md:flex md:w-auto md:gap-1">
              <ActionButton action="approve" labelVisibility="always" disabled={!hasFiles} onClick={() => review('approved')} />
              <ActionButton action="reject" label="Recusar" labelVisibility="always" onClick={() => review('rejected')} />
            </div>
          )}
        </div>
      </div>

      <div className="mt-2 grid grid-cols-2 gap-3 sm:flex sm:flex-wrap">
        {sidesOf(item).map((side) => {
          const file = item.files.find((f) => f.side === side);
          return (
            <div key={side} className="w-full sm:w-36">
              <p className="text-[11px] text-gray-500 mb-1">{SIDE_LABELS[side]}</p>
              {busySide === side ? (
                <div className="h-32 sm:h-24 rounded border border-gray-200 flex items-center justify-center">
                  <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                </div>
              ) : file ? (
                <>
                  <a
                    href={apiFileUrl(file.url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block h-32 sm:h-24 rounded border border-gray-200 overflow-hidden bg-gray-50"
                    title={`Enviado por ${file.uploadedBy === 'admin' ? 'equipe' : 'cliente'} em ${formatDate(file.uploadedAt)}`}
                  >
                    {file.mime === 'application/pdf' ? (
                      <span className="w-full h-full flex items-center justify-center text-gray-500">
                        <FileText className="w-8 h-8" />
                      </span>
                    ) : (
                      <img src={apiFileUrl(file.url)} alt={`${text.label} — ${SIDE_LABELS[side]}`} className="w-full h-full object-cover" />
                    )}
                  </a>
                  <div className="flex items-center gap-1 mt-1">
                    <ActionButton
                      action="copyLink"
                      label={`Copiar link temporário (${SHARE_LINK_MINUTES} min)`}
                      onClick={() => copyTempLink(file)}
                      className="flex-1 !flex-row !min-h-[44px] md:!min-h-0"
                    />
                    {reviewStatus !== 'approved' && (
                      <IconButton action="delete" label="Remover arquivo" onClick={() => removeFile(file)} />
                    )}
                  </div>
                </>
              ) : reviewStatus === 'approved' ? (
                <div className="h-32 sm:h-24 rounded border border-dashed border-gray-200" />
              ) : (
                <button
                  type="button"
                  onClick={() => pickFor(side)}
                  className="w-full h-32 sm:h-24 rounded border border-dashed border-gray-300 text-xs text-gray-500 hover:bg-gray-50 flex flex-col items-center justify-center gap-1"
                >
                  <Upload className="w-4 h-4" />
                  Enviar pela equipe
                </button>
              )}
            </div>
          );
        })}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={item.acceptPdf ? 'image/jpeg,image/png,image/webp,application/pdf' : 'image/jpeg,image/png,image/webp'}
        className="hidden"
        onChange={upload}
      />
    </li>
  );
};

interface ExtraItem {
  label: string;
  sides: 'single' | 'front_back';
}

const NewChecklistForm: React.FC<{
  clienteId: string;
  clienteEmail?: string;
  onCancel: () => void;
  onCreated: (doc: AdminChecklist) => void;
}> = ({ clienteId, clienteEmail, onCancel, onCreated }) => {
  const { t } = useTranslation();
  const notifications = useNotifications();
  const [templates, setTemplates] = useState<ChecklistTemplate[]>([]);
  const [visaType, setVisaType] = useState<VisaType>('trabalho_subordinado');
  const [lang, setLang] = useState<Lang>('pt');
  const [expiresInDays, setExpiresInDays] = useState(30);
  const [email, setEmail] = useState(clienteEmail || '');
  const [sendEmail, setSendEmail] = useState(false);
  const [retentionDays, setRetentionDays] = useState<number | ''>('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [extras, setExtras] = useState<ExtraItem[]>([]);
  const [extraLabel, setExtraLabel] = useState('');
  const [extraFrontBack, setExtraFrontBack] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios
      .get<ChecklistTemplate[]>('/documentos-cliente/templates')
      .then((res) => setTemplates(res.data))
      .catch(() => notifications.error.network());
    axios
      .get<{ retentionDays: number }>('/documentos-cliente/settings')
      .then((res) => setRetentionDays(res.data.retentionDays))
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const template = templates.find((tpl) => tpl.visaType === visaType);

  useEffect(() => {
    setSelected(new Set((template?.items || []).map((i) => i.key)));
  }, [template]);

  const toggle = (key: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const addExtra = () => {
    if (!extraLabel.trim()) return;
    setExtras((prev) => [...prev, { label: extraLabel.trim(), sides: extraFrontBack ? 'front_back' : 'single' }]);
    setExtraLabel('');
    setExtraFrontBack(false);
  };

  const submit = async () => {
    setSaving(true);
    try {
      const res = await axios.post<AdminChecklist>('/documentos-cliente', {
        clienteId,
        visaType,
        lang,
        expiresInDays,
        clientEmailHint: email.trim() || undefined,
        sendEmail: sendEmail && !!email.trim(),
        itemKeys: Array.from(selected),
        extraItems: extras,
        retentionDays: retentionDays || undefined,
      });
      notifications.showSuccess(
        'Checklist criado',
        sendEmail && email.trim() ? 'O link foi enviado por e-mail ao cliente.' : 'Copie o link ou a mensagem para enviar ao cliente.'
      );
      onCreated(res.data);
    } catch (err) {
      notifications.showError('Erro', errorMessage(err, 'Não foi possível criar o checklist.'));
    } finally {
      setSaving(false);
    }
  };

  const inputClass =
    'field';

  return (
    <div className="mb-5 border border-primary-200 bg-primary-50/40 rounded-lg p-4 space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <label className="text-sm">
          <span className="block text-gray-700 mb-1">Tipo de visto</span>
          <select value={visaType} onChange={(e) => setVisaType(e.target.value as VisaType)} className={`${inputClass} field--select`}>
            {VISA_TYPES.map((v) => (
              <option key={v} value={v}>
                {t(`visaForm.goals.${v}`)}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm">
          <span className="block text-gray-700 mb-1">Idioma do cliente</span>
          <select value={lang} onChange={(e) => setLang(e.target.value as Lang)} className={`${inputClass} field--select`}>
            <option value="pt">Português</option>
            <option value="en">English</option>
            <option value="es">Español</option>
          </select>
        </label>
        <label className="text-sm">
          <span className="block text-gray-700 mb-1">Validade do link (dias)</span>
          <input
            type="number"
            min={1}
            max={180}
            value={expiresInDays}
            onChange={(e) => setExpiresInDays(Math.min(180, Math.max(1, Number(e.target.value) || 30)))}
            className={inputClass}
          />
        </label>
      </div>

      <div>
        <p className="text-sm text-gray-700 mb-2">Documentos</p>
        {!template ? (
          <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
        ) : (
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {template.items.map((item) => (
              <li key={item.key}>
                <label className="flex items-start gap-2 text-sm text-gray-800">
                  <input
                    type="checkbox"
                    checked={selected.has(item.key)}
                    onChange={() => toggle(item.key)}
                    className="mt-0.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span>
                    {item.i18n.pt.label}
                    {item.sides === 'front_back' && <span className="text-gray-500"> (frente e verso)</span>}
                  </span>
                </label>
              </li>
            ))}
            {extras.map((x, idx) => (
              <li key={`extra-${idx}`} className="flex items-center gap-2 text-sm text-gray-800">
                <Check className="w-4 h-4 text-primary-600" />
                <span className="flex-1">
                  {x.label}
                  {x.sides === 'front_back' && <span className="text-gray-500"> (frente e verso)</span>}
                </span>
                <IconButton action="remove" onClick={() => setExtras((prev) => prev.filter((_, i) => i !== idx))} />
              </li>
            ))}
          </ul>
        )}
        <div className="mt-3 flex flex-col sm:flex-row gap-2">
          <input
            value={extraLabel}
            onChange={(e) => setExtraLabel(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addExtra())}
            placeholder="Documento extra só para este cliente (ex.: Procuração)"
            className={`${inputClass} flex-1`}
          />
          <label className="flex items-center gap-1.5 text-sm text-gray-700 whitespace-nowrap">
            <input type="checkbox" checked={extraFrontBack} onChange={(e) => setExtraFrontBack(e.target.checked)} />
            Frente e verso
          </label>
          <Button variant="ghost" size="sm" type="button" onClick={addExtra}>
            Adicionar
          </Button>
        </div>
      </div>

      <label className="text-sm block sm:max-w-xs">
        <span className="block text-gray-700 mb-1">Eliminar arquivos após a conclusão (dias)</span>
        <input
          type="number"
          min={1}
          max={365}
          value={retentionDays}
          onChange={(e) => setRetentionDays(e.target.value === '' ? '' : Math.min(365, Math.max(1, Number(e.target.value) || 1)))}
          className={inputClass}
        />
        <span className="block text-xs text-gray-500 mt-1">O prazo aparece no termo que o cliente aceita.</span>
      </label>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-end">
        <label className="text-sm">
          <span className="block text-gray-700 mb-1">E-mail do cliente</span>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputClass} />
        </label>
        <label className="flex items-center gap-2 text-sm text-gray-700 pb-2">
          <input type="checkbox" checked={sendEmail} disabled={!email.trim()} onChange={(e) => setSendEmail(e.target.checked)} />
          Enviar o link por e-mail agora
        </label>
      </div>

      <div className="form-actions">
        <Button variant="ghost" size="sm" type="button" onClick={onCancel}>
          Cancelar
        </Button>
        <Button size="sm"
          type="button"
          onClick={submit}
          disabled={saving || selected.size + extras.length === 0}>
          {saving ? 'Criando...' : 'Criar checklist e gerar link'}
        </Button>
      </div>
    </div>
  );
};

export default ClienteDocumentosSection;
